/**
 * The site facts a resident's settings are edited against, fetched once.
 *
 * The resident's own record carries only `{ name, uuid }`, so the numeric id, the coordinates and
 * the site's default perimeter all have to be fetched — and every one of them is needed by a
 * control further down. Fetched here rather than by those controls so there is one call per fact:
 * `useSite` was being called twice for the same site, once for its id and once for its
 * coordinates, and the zone list three times. React Query deduplicated the requests, so nothing
 * was slow; it just meant no single place said what this page needs to know about the site.
 *
 * The site is named explicitly, which the controls could not do. `useSiteConfiguration()` with no
 * argument answers for *the caller's own* site, so a sysadmin opening a resident on another site
 * was being offered their own site's default perimeter. `null` rather than `undefined` for a
 * resident with no site: that still means "a named site", just not one to fetch.
 */

import { useZonePresets } from '~modules/zone-presets/useZonePresets';
import { useSite } from '~hooks/site';
import { useSiteConfiguration } from '~containers/ConfigurationForm/ConfigurationForm';
import GeoJSON from 'geojson';

export interface ResidentSite {
  uuid?: string;
  /** The numeric id the zone endpoints are keyed on, once the site has loaded. */
  id?: number;
  lat?: number;
  long?: number;
  presets: ReturnType<typeof useZonePresets>['data'];
  /** The perimeter a new one is drawn from, so a carer starts at the building rather than blank. */
  defaultPerimeter?: GeoJSON.Feature<GeoJSON.Polygon>;
}

export const useResidentSite = (siteUuid?: string): ResidentSite => {
  const site = useSite(siteUuid);
  const { data: presets } = useZonePresets(site?.id);
  const { data: siteConfig } = useSiteConfiguration(siteUuid ?? null);

  return {
    uuid: siteUuid,
    id: site?.id,
    lat: site?.lat,
    long: site?.long,
    presets,
    defaultPerimeter: siteConfig?.['default-outdoor-geofencing-geo'],
  };
};
